import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import tokenService from '../../services/auth/tokenService';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  alpha,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock
} from '@mui/icons-material';
import MicrosoftIcon from '@mui/icons-material/Microsoft';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Preserve the full target path including query string (e.g. /map-view?assetId=...) through login.
  const fromLoc = location.state?.from;
  const from = fromLoc ? `${fromLoc.pathname}${fromLoc.search || ''}` : '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Handle SSO callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoSuccess = urlParams.get('sso_success');
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    
    if (error) {
      let errorMessage = 'SSO login failed. Please try again.';
      switch (error) {
        case 'invalid_domain':
          errorMessage = 'Only @plus.com.my emails are allowed for SSO login.';
          break;
        case 'authorization_denied':
          errorMessage = 'Microsoft login was cancelled or denied.';
          break;
        case 'sso_processing_failed':
          errorMessage = 'Failed to process SSO login. Please try again.';
          break;
      }
      setErrors({ general: errorMessage });
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (ssoSuccess === 'true' && token) {
      // Store token using the correct tokenService method
      console.log('🔐 [LoginPage] SSO success, storing token and triggering auth refresh');
      tokenService.setTokens(token, null);
      
      // Clean URL and let AuthContext handle the navigation
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Trigger a page reload to let AuthContext reinitialize with the new token
      window.location.reload();
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await login(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      setErrors({
        general: error.message || 'Invalid credentials. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/sso/login`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundImage: 'url(/BG_PLUS.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha('#000000', 0.3),
          zIndex: 0,
        }
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            p: { xs: 3, sm: 4 },
            backgroundColor: alpha('#ffffff', 0.95),
            borderRadius: 3,
            boxShadow: `0 20px 40px ${alpha('#000', 0.2)}`,
            border: `1px solid ${alpha('#000', 0.1)}`,
            backdropFilter: 'blur(8px)',
            zIndex: 2
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: 'primary.main',
                textAlign: 'center'
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                fontWeight: 400
              }}
            >
              Sign in to your IRIS account
            </Typography>
          </Box>

          {/* Error Alert */}
          {errors.general && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                border: 'none',
                backgroundColor: alpha('#ffebee', 0.9),
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              {errors.general}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isLoading}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha('#ffffff', 0.7),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password}
              disabled={isLoading}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha('#ffffff', 0.7),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: 'primary.main',
                boxShadow: `0 8px 24px ${alpha('#1976d2', 0.3)}`,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: `0 12px 32px ${alpha('#1976d2', 0.4)}`,
                  transform: 'translateY(-2px)'
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Microsoft SSO Section */}
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="textSecondary">OR</Typography>
              </Divider>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<MicrosoftIcon />}
                onClick={handleMicrosoftLogin}
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: '#0078d4',
                  color: '#0078d4',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderColor: '#106ebe',
                    backgroundColor: 'rgba(0, 120, 212, 0.04)',
                    transform: 'translateY(-1px)'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Sign in with Microsoft
              </Button>
              
              <Typography 
                variant="caption" 
                color="textSecondary" 
                sx={{ 
                  mt: 1, 
                  display: 'block', 
                  textAlign: 'center',
                  fontSize: '0.75rem'
                }}
              >
                For PLUS organization (@plus.com.my) users
              </Typography>
            </Box>
          </Box>

          {/* Footer Links */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Link
              to="/forgot-password"
              style={{ textDecoration: 'none' }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  mb: 2,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot your password?
              </Typography>
            </Link>

            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              Don't have an account?{' '}
              <Link
                to="/register"
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Sign up
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage; 